// Stripe Integration

import { config, state, MAX_STRIPE_RETRIES } from './config.js';
import { isValidEmail } from '../../shared/js/utils/index.js';
import { showFormError, clearFormError, clearFormData } from './forms.js';
import { resetTurnstile } from './turnstile.js';
import { showToast } from '../../shared/js/toast.js';

/**
 * Show page loading overlay during redirects
 */
function showPageLoadingOverlay(message = 'Redirecting...') {
    const overlay = document.getElementById('pageLoadingOverlay');
    if (overlay) {
        const textEl = overlay.querySelector('.page-loading-text');
        if (textEl) textEl.textContent = message;
        overlay.classList.add('active');
        overlay.setAttribute('aria-hidden', 'false');
    }
}

/**
 * Inject the Stripe.js script tag on demand
 */
function loadStripeScript() {
    if (document.querySelector('script[src*="js.stripe.com"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    document.head.appendChild(script);
}

/**
 * Initialize Stripe
 */
export function initializeStripe() {
    if (config.stripeKey && typeof Stripe !== 'undefined') {
        state.stripe = Stripe(config.stripeKey);
        state.stripeRetryCount = 0;
    } else if (config.stripeKey) {
        if (state.stripeRetryCount === 0) loadStripeScript();
        state.stripeRetryCount++;
        if (state.stripeRetryCount < MAX_STRIPE_RETRIES) {
            setTimeout(initializeStripe, 100);
        } else {
            console.error('Failed to load Stripe after', MAX_STRIPE_RETRIES, 'attempts');
            // Disable checkout buttons and show error state
            document.querySelectorAll('[onclick*="checkout"]').forEach(btn => {
                btn.disabled = true;
                btn.classList.add('stripe-unavailable');
                btn.title = 'Payment system unavailable - please refresh the page';
            });
            // Show toast notification
            showToast('Payment system unavailable. Please refresh the page to try again.', 'error');
        }
    }
}

/**
 * Handle checkout form submission
 */
export async function handleCheckout(event) {
    event.preventDefault();
    const email = document.getElementById('checkoutEmail').value.trim();
    const name = document.getElementById('checkoutName').value.trim();
    const company = document.getElementById('checkoutCompany').value.trim();
    const button = document.getElementById('checkoutBtn');

    clearFormError('checkoutEmail', 'checkoutEmailError');
    clearFormError('checkoutTurnstile', 'checkoutTurnstileError');

    if (!email) {
        showFormError('checkoutEmail', 'checkoutEmailError', 'Email address is required');
        document.getElementById('checkoutEmail').focus();
        return;
    }

    if (!isValidEmail(email)) {
        showFormError('checkoutEmail', 'checkoutEmailError', 'Please enter a valid email address');
        document.getElementById('checkoutEmail').focus();
        return;
    }

    if (!state.selectedEdition) {
        showToast('Please select a plan', 'error');
        return;
    }

    let turnstileToken = null;
    if (config.turnstileSiteKey && typeof turnstile !== 'undefined' && state.checkoutTurnstileWidgetId !== null) {
        turnstileToken = turnstile.getResponse(state.checkoutTurnstileWidgetId);
        if (!turnstileToken) {
            showFormError('checkoutTurnstile', 'checkoutTurnstileError', 'Please complete the security verification');
            return;
        }
    }

    button.disabled = true;
    button.classList.add('loading');

    try {
        const editionNames = { 1: 'Starter', 2: 'Professional', 3: 'Enterprise' };
        const editionName = editionNames[state.selectedEdition];

        const checkoutRequest = {
            email: email,
            name: name || undefined,
            company: company || undefined,
            edition: editionName,
            billingPeriod: state.isAnnualBilling ? 'annual' : 'monthly',
            turnstileToken: turnstileToken || undefined
        };

        if (state.selectedEdition === 2 && state.extraSeats > 0) {
            checkoutRequest.extraSeats = state.extraSeats;
        }

        const response = await fetch('/stripe/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(checkoutRequest)
        });

        if (!response.ok) {
            let errorMessage = 'Failed to create checkout session';
            try {
                const error = await response.json();
                errorMessage = error.error || errorMessage;
            } catch (e) {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        let data;
        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Invalid response from server');
        }

        if (data.url) {
            clearFormData('checkoutForm');
            showPageLoadingOverlay('Redirecting to checkout...');
            window.location.href = data.url;
        } else if (state.stripe && data.sessionId) {
            clearFormData('checkoutForm');
            showPageLoadingOverlay('Redirecting to checkout...');
            const result = await state.stripe.redirectToCheckout({ sessionId: data.sessionId });
            if (result.error) {
                throw new Error(result.error.message);
            }
        } else {
            throw new Error('Invalid checkout response');
        }
    } catch (error) {
        console.error('Checkout error:', error);
        showToast(error.message || 'Checkout failed. Please try again.', 'error');
        if (state.checkoutTurnstileWidgetId !== null && typeof turnstile !== 'undefined') {
            turnstile.reset(state.checkoutTurnstileWidgetId);
        }
    } finally {
        button.disabled = false;
        button.classList.remove('loading');
    }
}

/**
 * Handle download form submission
 */
export async function handleDownload(event) {
    event.preventDefault();

    const email = document.getElementById('downloadEmail').value.trim();
    const name = document.getElementById('downloadName').value.trim();
    const company = document.getElementById('downloadCompany').value.trim();
    const button = document.getElementById('downloadBtn');

    clearFormError('downloadEmail', 'downloadEmailError');
    clearFormError('downloadTurnstile', 'downloadTurnstileError');

    if (!email) {
        showFormError('downloadEmail', 'downloadEmailError', 'Email address is required');
        document.getElementById('downloadEmail').focus();
        return;
    }

    if (!isValidEmail(email)) {
        showFormError('downloadEmail', 'downloadEmailError', 'Please enter a valid email address');
        document.getElementById('downloadEmail').focus();
        return;
    }

    let turnstileToken = null;
    if (config.turnstileSiteKey && typeof turnstile !== 'undefined') {
        turnstileToken = turnstile.getResponse(state.downloadTurnstileWidgetId);
        if (!turnstileToken) {
            showFormError('downloadTurnstile', 'downloadTurnstileError', 'Please complete the security verification');
            return;
        }
    }

    button.disabled = true;
    button.classList.add('loading');

    try {
        const response = await fetch('/stripe/trial', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: email,
                name: name || undefined,
                company: company || undefined,
                turnstileToken: turnstileToken || undefined
            })
        });

        if (!response.ok) {
            let errorMessage = 'Failed to request trial';
            try {
                const error = await response.json();
                errorMessage = error.error || errorMessage;
            } catch (e) {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        let data;
        try {
            data = await response.json();
        } catch (e) {
            throw new Error('Invalid response from server');
        }

        if (data.downloadUrl) {
            clearFormData('downloadForm');
            window.location.href = data.downloadUrl;
        } else {
            showToast('Trial license created! Check your email for the download link.', 'success');
            document.getElementById('downloadForm').reset();
            clearFormData('downloadForm');
            resetTurnstile();
        }
    } catch (error) {
        console.error('Download error:', error);
        showToast(error.message || 'Failed to request trial. Please try again.', 'error');
        resetTurnstile();
    } finally {
        button.disabled = false;
        button.classList.remove('loading');
    }
}

/**
 * Handle enterprise inquiry form submission
 */
export async function handleEnterpriseInquiry(event) {
    event.preventDefault();

    const button = document.getElementById('enterpriseBtn');
    const companyName = document.getElementById('enterpriseCompany').value.trim();
    const contactName = document.getElementById('enterpriseName').value.trim();
    const contactEmail = document.getElementById('enterpriseEmail').value.trim();
    const phone = document.getElementById('enterprisePhone').value.trim();
    const estimatedSeats = document.getElementById('enterpriseSeats').value;
    const requirements = document.getElementById('enterpriseRequirements').value.trim();

    // Clear errors
    ['enterpriseCompany', 'enterpriseName', 'enterpriseEmail', 'enterpriseSeats', 'enterpriseTurnstile'].forEach(field => {
        clearFormError(field, field + 'Error');
    });

    let hasErrors = false;

    if (!companyName) {
        showFormError('enterpriseCompany', 'enterpriseCompanyError', 'Company name is required');
        hasErrors = true;
    }

    if (!contactName) {
        showFormError('enterpriseName', 'enterpriseNameError', 'Your name is required');
        hasErrors = true;
    }

    if (!contactEmail) {
        showFormError('enterpriseEmail', 'enterpriseEmailError', 'Email address is required');
        hasErrors = true;
    } else if (!isValidEmail(contactEmail)) {
        showFormError('enterpriseEmail', 'enterpriseEmailError', 'Please enter a valid email address');
        hasErrors = true;
    }

    if (!estimatedSeats) {
        showFormError('enterpriseSeats', 'enterpriseSeatsError', 'Please select estimated seats');
        hasErrors = true;
    }

    let turnstileToken = null;
    if (config.turnstileSiteKey && typeof turnstile !== 'undefined' && state.enterpriseTurnstileWidgetId !== null) {
        turnstileToken = turnstile.getResponse(state.enterpriseTurnstileWidgetId);
        if (!turnstileToken) {
            showFormError('enterpriseTurnstile', 'enterpriseTurnstileError', 'Please complete the security verification');
            hasErrors = true;
        }
    }

    if (hasErrors) return;

    button.disabled = true;
    button.classList.add('loading');

    try {
        const response = await fetch('/api/inquiries/enterprise', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                companyName,
                contactName,
                contactEmail,
                phone: phone || undefined,
                estimatedSeats: parseInt(estimatedSeats),
                requirements: requirements || undefined,
                turnstileToken: turnstileToken || undefined
            })
        });

        if (!response.ok) {
            let errorMessage = 'Failed to submit inquiry';
            try {
                const error = await response.json();
                errorMessage = error.error || errorMessage;
            } catch (e) {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        // Import closeEnterpriseModal dynamically to avoid circular dependency
        const { closeEnterpriseModal } = await import('./modals.js');
        closeEnterpriseModal();
        showToast('Thanks for your interest! We\'ll be in touch within 1 business day.', 'success');

    } catch (error) {
        console.error('Enterprise inquiry error:', error);
        showToast(error.message || 'Failed to submit inquiry. Please try again.', 'error');
        if (state.enterpriseTurnstileWidgetId !== null && typeof turnstile !== 'undefined') {
            turnstile.reset(state.enterpriseTurnstileWidgetId);
        }
    } finally {
        button.disabled = false;
        button.classList.remove('loading');
    }
}
